import { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { Divider } from 'primereact/divider';
import { useDebounce } from 'primereact/hooks';
import { useWindowSize } from 'react-use';
import { CommonCtx } from '../../../../context/CommonCtxProvider';
import { useQuery, useQueryClient } from 'react-query';
import { AuthCtx } from '../../../../context/AuthCtxProvider';
import { MOBILE_BREAKPOINT, OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../constants/constants';
import {
    _DatatablePageEvent,
    _Pagination,
    _PermissionGroupOption,
    _ToastMessageProps,
    _VenuePickerOption,
} from '../../../../constants/staticTypes';
import { defaultPaginationEvent, defaultToastData } from '../../../../helpers/commonHelper';
import MlxButton from '../../../../components/common/MlxButton/MlxButton';
import MlxSearch from '../../../../components/common/MlxSearch/MlxSearch';
import withSidebar from '../../../../hoc/withSideBar/withSideBar';
import logger from '../../../../utils/logger';
import { getVenueOptions } from '../../../../helpers/venueHelper';
import { ADD_USER, COPY_USERS_FROM, COPY_USERS_TO, MANAGE_USERS, SELECT_VENUE, USERS_LIST } from '../../../../constants/strings';
import { useTranslation } from '../../../../hooks/useTranslation';
import VenueUsersList from './components/VenueUsersList';
import { VENUE_LIST_USERS } from '../../../../constants/queryKeys';
import { _AddUserVenueFormType, _JobRole, _VenueUser, getVenueJobRolesList, getVenueUsersList } from '../../../../helpers/venueUsersHelper';
import MlxDropDown from '../../../../components/common/MlxDropDown/MlxDropDown';
import AddVenueUserSidebar from './components/AddVenueUserSidebar';
import { getVenuePermissionGroupOptions } from '../../../../helpers/venuePermissionHelper';
import MlxBlockUI from '../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxToolTip from '../../../../components/common/MlxToolTip/MlxToolTip';
import { ConfirmPopup } from 'primereact/confirmpopup';
import MlxToast from '../../../../components/common/MlxToast/MlxToast';
import CopyVenueUsersModal from './components/CopyVenueUsersModal';
import { checkActionSubPermission } from '../../../../utils/common';

const searchColumns = ['firstName', 'lastName', 'email'];

const languageTexts = {
    MANAGE_USERS: MANAGE_USERS,
    USERS_LIST: USERS_LIST,
    ADD_USER: ADD_USER,
    SELECT_VENUE: SELECT_VENUE,
    COPY_USERS_TO: COPY_USERS_TO,
    COPY_USERS_FROM: COPY_USERS_FROM,
};

function VenueUsers() {
    const { width: screenWidth } = useWindowSize();
    const { showSideBar, setShowSideBar } = useContext(CommonCtx);
    const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce('', 1200);
    const queryClient = useQueryClient();
    const { userType } = useContext(AuthCtx);
    const translatedText = useTranslation(languageTexts, 'none');
    const [popupTarget, setPopupTarget] = useState<any>(null);

    const [searchBarVisible, setSearchBarVisible] = useState(screenWidth > MOBILE_BREAKPOINT);
    const [venueUserAdded, setVenueUserAdded] = useState<boolean>(false);
    const [isVenueUserEdit, setIsVenueUserEdit] = useState<boolean>(false);
    const [venueUserEditData, setVenueUserEditData] = useState<_AddUserVenueFormType>();
    const [dataTableEvent, setDataTableEvent] = useState<_DatatablePageEvent>(defaultPaginationEvent);
    const [venueUsersListData, setVenueUsersListData] = useState<any>(null);
    const [venueOptions, setVenueOptions] = useState<_VenuePickerOption[]>([]);
    const [selectedVenueOption, setSelectedVenueOption] = useState<string>();
    const [venuePermissionGroups, setVenuePermissionGroups] = useState<any>([]);
    const [venueDefaultPermissionGroup, setVenueDefaultPermissionGroup] = useState<any>([]);
    const [venuePermissionGroupsLoading, setVenuePermissionGroupsLoading] = useState<boolean>(false);
    const [venueJobRoles, setVenueJobRoles] = useState<any>([]);
    const [venueJobRolesLoading, setVenueJobRolesLoading] = useState<boolean>(false);
    const [isVenueListLoading, setIsVenueListLoading] = useState<boolean>(false);
    const [copyPopupVisible, setCopyPopupVisible] = useState(false);
    const [copyType, setCopyType] = useState<'copy-from' | 'copy-to' | ''>('');
    const [copyFromVenueModalVisible, setCopyFromVenueModalVisible] = useState(false);
    const [selectedCopyVenueOption, setSelectedCopyVenueOption] = useState<string>();
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);

    useEffect(() => {
        setIsVenueListLoading(true);
        const getVenues = async () => {
            const venueOptionsList = await getVenueOptions(queryClient);
            setVenueOptions(venueOptionsList);
            venueOptionsList?.length > 0 && setSelectedVenueOption(venueOptionsList[0]?.id);
            setIsVenueListLoading(false);
        };
        getVenues();
    }, []);

    const venueUsersListQuery = useQuery({
        queryKey: VENUE_LIST_USERS,
        queryFn: () => getVenueUsersList(queryClient, (dataTableEvent.page ?? 0) + 1, 500, dataTableEvent.search, selectedVenueOption),
        onSuccess: (response) => {
            const paginationData = response?.data?.data?.pagination as _Pagination;
            const responseData = response?.data?.data?.lists as _VenueUser;
            setVenueUsersListData(responseData);
            setDataTableEvent((event) => ({ ...event, totalRows: paginationData?.total }));
        },
        onError: (error) => logger.log('Failed to fetch admin staff list', error),
        refetchOnWindowFocus: false,
        enabled: false,
    });

    useEffect(() => {
        if (!selectedVenueOption) return;
        venueUsersListQuery.refetch();
        getPermissionGroupsForVenue(selectedVenueOption);
        getJobRolesForVenue(selectedVenueOption);
    }, [selectedVenueOption]);

    const venueListQueryRef = useRef(venueUsersListQuery);

    useEffect(() => {
        if (!selectedVenueOption) return;
        venueListQueryRef.current.refetch();
    }, [dataTableEvent?.forceUpdate]);

    useEffect(() => {
        const searchPayload = searchColumns.map((column) => `&search[${column}]=${debouncedSearchValue}`).join('');
        setDataTableEvent((event) => ({
            ...event,
            search: debouncedSearchValue.trim().length > 0 ? searchPayload : '',
            forceUpdate: event.forceUpdate + 1,
        }));
    }, [debouncedSearchValue]);

    const openEditVenueSidebar = (editableData: _AddUserVenueFormType) => {
        setVenueUserEditData(editableData);
        setIsVenueUserEdit(true);
        setShowSideBar(true);
    };

    const getPermissionGroupsForVenue = async (venueId: any) => {
        setVenuePermissionGroupsLoading(true);
        try {
            const response = await getVenuePermissionGroupOptions(queryClient, venueId);
            const venuePermissionData = (response?.data?.data?.lists ?? []) as _PermissionGroupOption[];

            const permissionOptions = venuePermissionData?.map((item) => ({
                permissionRoleName: item.permissionRoleName,
                id: item.id,
            }));
            setVenuePermissionGroups(permissionOptions);

            const defaultPermission = venuePermissionData.find((ven) => ven.isDefault === true);
            setVenueDefaultPermissionGroup({
                id: defaultPermission?.id ?? '',
                permissionRoleName: defaultPermission?.permissionRoleName ?? '',
            });

            setVenuePermissionGroupsLoading(false);
        } catch (error) {
            console.error('Failed to fetch permission groups:', error);
        }
    };

    const getJobRolesForVenue = async (venueId: string) => {
        setVenueJobRolesLoading(true);
        try {
            const response = await getVenueJobRolesList(queryClient, venueId);
            const jobRoleData = response?.data?.data?.lists as _JobRole[];
            setVenueJobRoles(jobRoleData);
        } catch (error) {
            console.error('Failed to fetch job roles:', error);
        }
        setVenueJobRolesLoading(false);
    };

    useEffect(() => {
        if (venueUserAdded) {
            venueUsersListQuery.refetch();
            setVenueUserAdded(false);
        }
    }, [venueUserAdded]);

    const confirmCopyVenue = (event: any) => {
        setPopupTarget(event.currentTarget);
        setCopyPopupVisible(true);
    };

    const onCopyFromSuccess = () => {
        setCopyFromVenueModalVisible(false);
        venueUsersListQuery.refetch();
        setSelectedCopyVenueOption('');
    };

    return (
        <MlxBlockUI opacity={0.5}>
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <ConfirmPopup
                target={popupTarget}
                visible={copyPopupVisible}
                onHide={() => {
                    setCopyPopupVisible(false);
                }}
                message={
                    <>
                        <div className="mb-2">{copyType === 'copy-to' ? translatedText.COPY_USERS_TO : translatedText.COPY_USERS_FROM}</div>
                        <MlxDropDown
                            dropDownSize="large"
                            options={venueOptions?.filter((item) => item.id !== selectedVenueOption && item.isActive)}
                            optionLabel="venueName"
                            optionValue="id"
                            value={selectedCopyVenueOption}
                            placeholder={translatedText.SELECT_VENUE}
                            onChange={(e) => {
                                e.preventDefault();
                                setSelectedCopyVenueOption(e.value);
                            }}
                            style={{ width: '200px' }}
                        />
                    </>
                }
                acceptLabel="Proceed"
                rejectLabel="Cancel"
                accept={() => setCopyFromVenueModalVisible(true)}
                reject={() => {}}
                acceptClassName={`p-button ${!selectedCopyVenueOption ? 'p-disabled' : ''}`}
            />
            {copyFromVenueModalVisible && (
                <CopyVenueUsersModal
                    visible={true}
                    setVisible={setCopyFromVenueModalVisible}
                    selectedVenueOption={selectedVenueOption}
                    selectedCopyVenueOption={selectedCopyVenueOption}
                    onCopyFromSuccess={onCopyFromSuccess}
                    venueOptions={venueOptions}
                    setToastMessage={setToastMessage}
                    copyType={copyType}
                />
            )}

            <Suspense fallback={<div>Loading...</div>}>
                <AddVenueUserSidebar
                    showSideBar={showSideBar}
                    setShowSideBar={setShowSideBar}
                    selectedVenueOption={selectedVenueOption}
                    venuePermissionGroups={venuePermissionGroups}
                    venuePermissionGroupsLoading={venuePermissionGroupsLoading}
                    venueDefaultPermissionGroup={venueDefaultPermissionGroup}
                    isVenueUserEdit={isVenueUserEdit}
                    setIsVenueUserEdit={setIsVenueUserEdit}
                    venueUserEditData={venueUserEditData}
                    venueJobRoles={venueJobRoles}
                    venueJobRolesLoading={venueJobRolesLoading}
                    setVenueUserAdded={setVenueUserAdded}
                />
            </Suspense>

            <div className="page-section">
                <span className="page-header d-flex justify-content-between">
                    <span>{translatedText.MANAGE_USERS}</span>
                    <MlxDropDown
                        dropDownSize="large"
                        options={venueOptions}
                        optionLabel="venueName"
                        optionValue="id"
                        value={selectedVenueOption}
                        placeholder={translatedText.SELECT_VENUE}
                        onChange={(e) => {
                            setSelectedVenueOption(e.value);
                        }}
                        style={{ width: '200px' }}
                    />
                </span>
                <div className="page-content h-100">
                    <div className="content-header flex-grow-0">
                        <span className="header-title">{searchBarVisible ? translatedText.USERS_LIST : ''}</span>
                        <div className="header-fields">
                            {(userType === 'organizationAdmin' ||
                                checkActionSubPermission(
                                    queryClient,
                                    OrgPermissionKeys.MANAGE_VENUE_STAFF,
                                    OrgSubPermissionKeys.ADD_VENUE_STAFF
                                )) && (
                                <>
                                    <MlxToolTip target={'.copyFromButton'}>
                                        <MlxButton
                                            className="bulk-copy-button copyFromButton"
                                            size="small"
                                            outlined
                                            onClick={(e) => {
                                                setCopyType('copy-from');
                                                confirmCopyVenue(e);
                                            }}
                                            data-pr-tooltip="Copy users from another venue"
                                            data-pr-position="bottom"
                                        >
                                            <i className="pi pi-file-import"></i>
                                        </MlxButton>
                                    </MlxToolTip>
                                    <MlxToolTip target={'.copyToButton'}>
                                        <MlxButton
                                            className="bulk-copy-button copyToButton"
                                            size="small"
                                            outlined
                                            onClick={(e) => {
                                                setCopyType('copy-to');
                                                confirmCopyVenue(e);
                                            }}
                                            data-pr-tooltip="Copy users to another venue"
                                            data-pr-position="bottom"
                                        >
                                            <i className="pi pi-file-export"></i>
                                        </MlxButton>
                                    </MlxToolTip>
                                </>
                            )}
                            {(userType === 'organizationAdmin' ||
                                checkActionSubPermission(
                                    queryClient,
                                    OrgPermissionKeys.MANAGE_VENUE_STAFF,
                                    OrgSubPermissionKeys.ADD_VENUE_STAFF
                                )) && (
                                <MlxButton
                                    className="ms-2"
                                    removeLabelOnMobile={true}
                                    size="small"
                                    label={translatedText.ADD_USER}
                                    icon="ri-add-large-line"
                                    iconPos="right"
                                    onClick={() => setShowSideBar(true)}
                                />
                            )}
                            <MlxSearch
                                className="ms-2 p-inputtext-sm"
                                removeLabelOnMobile={true}
                                placeholder="Search"
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                                searchBarVisible={setSearchBarVisible}
                                isLoading={venueUsersListQuery?.isFetching && !!debouncedSearchValue.trim().length}
                            />
                        </div>
                    </div>
                    <Divider className="content-divider" />
                    <div className="content-body flex-grow-1 overflow-hidden">
                        <VenueUsersList
                            dataTableEvent={dataTableEvent}
                            setDataTableEvent={setDataTableEvent}
                            openEditVenueSidebar={openEditVenueSidebar}
                            venueUsersListData={venueUsersListData}
                            setVenueUsersListData={setVenueUsersListData}
                            venueUsersListQuery={venueUsersListQuery}
                            selectedVenueOption={selectedVenueOption}
                            isVenueListLoading={isVenueListLoading}
                        />
                    </div>
                </div>
            </div>
        </MlxBlockUI>
    );
}

export default withSidebar(VenueUsers);
